import type { Dispatch, FormEvent, SetStateAction } from 'react'
import React, { useEffect, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import {
  SelectSupplier,
  SelectCenter,
  SelectTypeCode,
  SelectTransportType,
  SearchCustomer,
  ActionButton,
  SearchButton,
  CanselButton,
} from 'components/commonparts'
import {
  FlexRowWrapForm,
  FlexRowAlignEndJustifyCenterBox,
  FlexRowGap1Box,
} from 'style'

import type { GetStockStockGetRequest } from 'api-client'
import type { MstTransportTypeSeq, MstTransportTypesSeq} from 'hooks/useTransportType';

interface SearchConditionProps {
  searchParam: GetStockStockGetRequest
  onSearch: (requestParam: GetStockStockGetRequest) => void
  hasDataEdited: boolean
  setDiscardChangeDialogOpen: Dispatch<SetStateAction<boolean>>
  searchTrigger?: boolean
  setTransportType: Dispatch<SetStateAction<any | null | undefined>>
  initialTransportType?: object

  setMinLoadCapacity: Dispatch<SetStateAction<any | null | undefined>>
  setCenterSupplierInfo: Dispatch<SetStateAction<any | null | undefined>>
  setOptimizeLoadFactorLogic: Dispatch<SetStateAction<any | null | undefined>>
  onTransportLot?: () => void
  onOptimizeLoadFactor?: () => void
  transportTypeSearch: MstTransportTypesSeq
  initialMinLoadCapacity?: number
  onValidateAndExecute?: (executeFn: (showErrors?: boolean) => boolean) => void
}

function getTransportTypeObject(
  transportTypeList: MstTransportTypeSeq[],
  listId: object | undefined
): object[] {
  if (!listId) return [];
  const ids = Object.values(listId);
  
  const result = transportTypeList.filter(item =>
    ids.includes(Number(item?.transportTypeId))
  );
  return result;
}

function isTransportSelectionEmpty(list: object | undefined): boolean {
  return (
    !list ||
    Object.values(list).length === 0 ||
    !Object.values(list).some((id) => id !== 0)
  )
}

function SearchCondition({
  searchParam,
  onSearch,
  setDiscardChangeDialogOpen,
  hasDataEdited,
  searchTrigger,
  setTransportType,
  initialTransportType,
  setMinLoadCapacity,
  setCenterSupplierInfo,
  setOptimizeLoadFactorLogic,
  onTransportLot,
  onOptimizeLoadFactor,
  transportTypeSearch,
  initialMinLoadCapacity,
  onValidateAndExecute
}: SearchConditionProps) {
  const { loginUserInfo } = useAuthContext()
  const [centerId, setCenterId] = useState<number | null | undefined>(searchParam.centerId)
  const [orgSupplierId, setOrgSupplierId] = useState(searchParam.orgSupplierId)
  const [transportTypeId, setTransportTypeId] = useState<object | undefined>()
  const [centerIsValid, setCenterIsValid] = useState(true)
  const [supplierIsValid, setSupplierIsValid] = useState(true)
  const [typeCodeIsValid, setTypeCodeIsValid] = useState(false)
  const [transportTypeIsValid, setTransportTypeIsValid] = useState(false)
  const [optimizeLoadFactorLogicIsValid, setOptimizeLoadFactorLogicIsValid] = useState(false)
  const [viewUnitTypeCode, setViewUnitTypeCode] = useState<string | null | undefined>(searchParam.viewUnitType)
  const [minLoadCapacityCode, setMinLoadCapacityCode] = useState<string | null | undefined>(
    initialMinLoadCapacity ? String(initialMinLoadCapacity) : undefined
  )
  const [optimizeLoadFactorLogicTypeCode, setOptimizeLoadFactorLogicTypeCode] = useState<string | null | undefined>(undefined)
  const [customerCompanyId, setCustomerCompanyId] = useState<number | undefined>(
    searchParam.companyId === loginUserInfo!.companyId ? undefined : searchParam.companyId
  )
  const [validateTransportRequested, setValidateTransportRequested] = useState(false)
  const [validateMinLoadCapacityRequested, setValidateMinLoadCapacityRequested] = useState(false)
  const [validateOptimizeRequested, setValidateOptimizeRequested] = useState(false)

  const onClear = () => {
    setCustomerCompanyId(undefined)
    setCenterId(undefined)
    // SelectSupplier clears only when orgSupplierId becomes undefined (see SelectSupplier.tsx)
    setOrgSupplierId(undefined)
    setCenterSupplierInfo(undefined)
    setTransportTypeId(undefined)
    setViewUnitTypeCode(undefined)
    setMinLoadCapacityCode(undefined)
    setOptimizeLoadFactorLogicTypeCode(undefined)
    setCenterIsValid(true)
    setSupplierIsValid(true)
    // Reset validation states
    setValidateTransportRequested(false)
    setValidateMinLoadCapacityRequested(false)
    setValidateOptimizeRequested(false)
    setTransportTypeIsValid(false)
    setTypeCodeIsValid(false)
    setOptimizeLoadFactorLogicIsValid(false)
  }

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Search only validates center/supplier; do not force transport/min-load validation here.
    setValidateTransportRequested(false)
    setValidateMinLoadCapacityRequested(false)
    setValidateOptimizeRequested(false)

    if (hasDataEdited) {
      if (setDiscardChangeDialogOpen) setDiscardChangeDialogOpen((prev) => !prev)
      return
    }
    if (!centerIsValid && !supplierIsValid) {
      onSearch({
        companyId: loginUserInfo!.companyId,
        centerId,
        orgSupplierId,
        viewUnitType: viewUnitTypeCode,
        orderPlanFlg: true,
      })
    }
  }

  const handleSetTransportTypeId = (next: object | undefined) => {
    // Turn off validation immediately when clearing selection to avoid "red flash"
    if (isTransportSelectionEmpty(next)) {
      setValidateTransportRequested(false)
      setTransportTypeIsValid(false)
    }
    setTransportTypeId(next)
  }

  useEffect(()=>{
    if (
      transportTypeId === undefined &&
      transportTypeSearch?.list &&
      transportTypeSearch.list.length > 0 &&
      initialTransportType
    ) {
      const transportTypes = getTransportTypeObject(transportTypeSearch.list, initialTransportType)
      if (transportTypes.length > 0) {
        setTransportTypeId(transportTypes)
      }
    }
  },[transportTypeSearch?.list, initialTransportType, transportTypeId])

  useEffect(() => {
    setTransportType(transportTypeId)
    setMinLoadCapacity(minLoadCapacityCode ? Number(minLoadCapacityCode) : "")
    setOptimizeLoadFactorLogic(optimizeLoadFactorLogicTypeCode)
  }, [transportTypeId, minLoadCapacityCode, optimizeLoadFactorLogicTypeCode])

  // If user clears all transport items, turn off validation highlight for that field
  useEffect(() => {
    if (isTransportSelectionEmpty(transportTypeId)) {
      setValidateTransportRequested(false)
      setTransportTypeIsValid(false)
    }
  }, [transportTypeId])

  useEffect(() => {
    // searchTriggerの変更を検知して、検索イベントを起こす
    // 親コンポーネントなどで検索を行いたい場合は、このトリガーを使用する
    // Keep transport-related validation only for transport actions.
    setValidateTransportRequested(false)
    setValidateMinLoadCapacityRequested(false)
    setValidateOptimizeRequested(false)

    if (!centerIsValid && !supplierIsValid) {
      onSearch({
        companyId: loginUserInfo!.companyId,
        centerId,
        orgSupplierId,
        viewUnitType: viewUnitTypeCode,
        orderPlanFlg: true,
      })
    }
  }, [searchTrigger])

  // Handle external validation triggering (for context menu actions)
  useEffect(() => {
    if (onValidateAndExecute) {
      const executeFn = (showErrors = true) => {
        const isTransportValid =
          transportTypeId &&
          Object.values(transportTypeId).length > 0 &&
          Object.values(transportTypeId).some(id => id !== 0);
        const isTypeCodeValid = minLoadCapacityCode !== '' && minLoadCapacityCode !== undefined
        const isValid = !!isTransportValid && isTypeCodeValid

        // Only set UI states if validation fails (to show errors)
        if (!isValid && showErrors) {
          setValidateTransportRequested(true)
          setValidateMinLoadCapacityRequested(true)
          setTransportTypeIsValid(!!isTransportValid)
          setTypeCodeIsValid(isTypeCodeValid)
        }

        return isValid
      }
      onValidateAndExecute(executeFn)
    }
  }, [onValidateAndExecute, transportTypeId, minLoadCapacityCode])

  const handleTransportLot = () => {
    setValidateTransportRequested(true)
    setValidateMinLoadCapacityRequested(true)
    const isTransportValid =
      transportTypeId &&
      Object.values(transportTypeId).length > 0 &&
      Object.values(transportTypeId).some(id => id !== 0);
    const isTypeCodeValid = minLoadCapacityCode !== '' && minLoadCapacityCode !== undefined

    setTransportTypeIsValid(isTransportValid!)
    setTypeCodeIsValid(isTypeCodeValid)

    if (isTransportValid && isTypeCodeValid && onTransportLot) {
      onTransportLot()
    }
  }

  const handleOptimizeLoadFactor = () => {
    setValidateTransportRequested(true)
    setValidateMinLoadCapacityRequested(true)
    setValidateOptimizeRequested(true)
    const isTransportValid =
      transportTypeId &&
      Object.values(transportTypeId).length > 0 &&
      Object.values(transportTypeId).some(id => id !== 0);
    const isTypeCodeValid = minLoadCapacityCode !== '' && minLoadCapacityCode !== undefined
    const isOptimizeLoadFactorLogicValid = !!optimizeLoadFactorLogicTypeCode

    setTransportTypeIsValid(isTransportValid!)
    setTypeCodeIsValid(isTypeCodeValid)
    setOptimizeLoadFactorLogicIsValid(isOptimizeLoadFactorLogicValid)

    if (isTransportValid && isTypeCodeValid && isOptimizeLoadFactorLogicValid && onOptimizeLoadFactor) {
      onOptimizeLoadFactor()
    }
  }

  return (
    <FlexRowWrapForm onSubmit={handleFormSubmit}>
      <SearchCustomer
        title="会社"
        trCompanyId={loginUserInfo!.companyId}
        customerCompanyId={customerCompanyId}
        setCustomerCompanyId={setCustomerCompanyId}
        setCustomerCompanyInfo={() => {}}
        isRequred={false}
        setIsValid={() => {}}
      />
      <SelectCenter
        trCompanyId={loginUserInfo?.companyId}
        centerId={centerId}
        setCenterId={setCenterId}
        setCenterInfo={() => {}}
        isRequired
        setIsValid={setCenterIsValid}
      />

      <SelectSupplier
        trCompanyId={searchParam.companyId}
        orgSupplierId={orgSupplierId}
        setOrgSupplierId={setOrgSupplierId}
        centerId={centerId}
        setSupplierInfo={setCenterSupplierInfo}
        isRequred
        setIsValid={setSupplierIsValid}
        enabled
        alignmentFlag
      />
      <FlexRowAlignEndJustifyCenterBox>
        <SearchButton searchId="item" />
        <CanselButton onClick={onClear} caption="クリア" disabled={false} />
      </FlexRowAlignEndJustifyCenterBox>
      <FlexRowGap1Box>
        <SelectTransportType
          trCompanyId={loginUserInfo!.companyId}
          searchParam={searchParam}
          transportTypeId={transportTypeId}
          setTransportTypeIDList={handleSetTransportTypeId}
          setTransportTypeInfoList={() => {}}
          isRequired={validateTransportRequested}
          setIsValid={setTransportTypeIsValid}
          enabled
        />
        <SelectTypeCode
          typeGroup="MinLoadCapacity"
          title="積載量下限設定"
          typeCode={minLoadCapacityCode}
          setTypeCode={setMinLoadCapacityCode}
          setTypeCodeInfo={() => {}}
          isRequired={validateMinLoadCapacityRequested}
          setIsValid={setTypeCodeIsValid}
          errorMessage="積載量下限設定を選択してください"
          selcetRequired={false}
        />
        <FlexRowAlignEndJustifyCenterBox>
          <ActionButton caption="車両割当" onClick={() => handleTransportLot()} />
        </FlexRowAlignEndJustifyCenterBox>
        <SelectTypeCode
          typeGroup="OptimizeLoadFactorLogic"
          title="積載率調整"
          typeCode={optimizeLoadFactorLogicTypeCode}
          setTypeCode={setOptimizeLoadFactorLogicTypeCode}
          setTypeCodeInfo={() => {}}
          isRequired={validateOptimizeRequested}
          setIsValid={setOptimizeLoadFactorLogicIsValid}
          errorMessage="積載率最適化設定を選択してください"
          selcetRequired={false}
        />
        <FlexRowAlignEndJustifyCenterBox>
          <ActionButton caption="発注日積載率調整" onClick={() => handleOptimizeLoadFactor()} />
        </FlexRowAlignEndJustifyCenterBox>
      </FlexRowGap1Box>
    </FlexRowWrapForm>
  )
}

export default SearchCondition
